// Third-party
import React from 'react';

// Components
import FilterControls from './FilterControls';

// Types
import SearchOptions from '@/types/search-options';
import FilterType from '@/types/filter-type';

type Props = {
  filters: FilterType[];
  loadingFilters: boolean;
  preLoadQuery: string;
  onSearch: (options: SearchOptions) => void;
  onFilterCountChange?: (count: number) => void;
  activeFilters: Record<string, unknown>;
  onFiltersChange: (filters: Record<string, unknown>) => void;
};

/**
 * Renders the appropriate filter component based on instrument type.
 * Shared across different screener pages.
 */
const ScreenerFilters = ({ filters, loadingFilters, preLoadQuery, onSearch, onFilterCountChange, activeFilters, onFiltersChange }: Props) => {
  const isFirstRender = React.useRef(true);

  const handleFilterChange = (value: unknown, filter: FilterType) => {
    // Extract value property from option objects (similar to AdvancedSearchInput)
    let extractedValue: unknown;

    if (Array.isArray(value)) {
      if (filter.multiselect) {
        // Multi-select: extract value from each option object
        extractedValue = value.map((option: { value: unknown }) => option.value);
      } else if (value.length > 0) {
        // Single-select: extract value from first option only
        extractedValue = (value[0] as { value: unknown }).value;
      }
    } else if (value && typeof value === 'object' && 'value' in value) {
      // Single-select: extract value from option object
      extractedValue = (value as { value: unknown }).value;
    } else {
      // Already a primitive value
      extractedValue = value;
    }

    const updatedFilters = { ...activeFilters };

    // If the filter is cleared (empty array or undefined), remove it from activeFilters
    if (extractedValue === undefined || extractedValue === null || extractedValue === '' || (Array.isArray(extractedValue) && extractedValue.length === 0)) {
      delete updatedFilters[filter.key];
    } else {
      updatedFilters[filter.key] = extractedValue;
    }

    onFiltersChange(updatedFilters);
  };

  const handleClearAll = () => {
    onFiltersChange({});
    // Trigger search with empty filters to reset results
    onSearch({ query: preLoadQuery || '' });
  };

  const formatFiltersForQuery = (filterValues: Record<string, unknown>): Record<string, unknown> =>
    Object.keys(filterValues).reduce((acc: Record<string, unknown>, key) => {
      const value = filterValues[key];

      // Handle range filters stored as [min, max] arrays
      if (Array.isArray(value) && value.length === 2) {
        const [minValue, maxValue] = value;
        if (minValue != null) {
          acc[`min${key}`] = minValue;
        }
        if (maxValue != null) {
          acc[`max${key}`] = maxValue;
        }
      } else {
        acc[key] = value;
      }

      return acc;
    }, {});

  const applySearch = () => {
    const formattedFilters = formatFiltersForQuery(activeFilters);
    const searchOptions: SearchOptions = {
      ...formattedFilters,
      query: preLoadQuery || '',
    };
    onSearch(searchOptions);
  };

  // Calculate active filters count
  const activeFiltersCount = React.useMemo(
    () =>
      Object.keys(activeFilters).reduce((total, key) => {
        const value = activeFilters[key];
        if (value == null || value === '') return 0;

        // Range filters are stored as [min, max] arrays where min/max can be numbers or null
        if (Array.isArray(value) && value.length === 2 && value.every(v => v === null || typeof v === 'number')) {
          return total + 1;
        }

        // Multi-select filters are arrays of values - count each value
        if (Array.isArray(value)) {
          return total + value.length;
        }

        // Otherwise count as 1
        return total + 1;
      }, 0),
    [activeFilters],
  );

  // Auto-apply search when filters change
  React.useEffect(() => {
    onFilterCountChange?.(activeFiltersCount);

    // Skip search on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    applySearch();
  }, [activeFilters, activeFiltersCount]);

  return (
    <div className="screener-filters">
      <FilterControls
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
        loadingFilters={loadingFilters}
        activeFiltersCount={activeFiltersCount}
        activeFilters={activeFilters}
      />
    </div>
  );
};

export default ScreenerFilters;
